/**
 * Sidebar.tsx — Left sidebar navigation (N-19).
 *
 * Renders the grouped navigation as a fixed left rail on desktop and as a
 * collapsible icon-only rail on tablet. Mobile renders the same content via
 * SidebarDrawer instead, so this component is desktop+tablet only.
 *
 * Active route highlight is computed from `pathname` against each item's
 * href/matchPrefixes. Brand sits at the top, user info + account menu at the
 * bottom so the working area in the middle stays uncluttered.
 *
 * Per-route visual emphasis follows the existing admin pattern: active item
 * gets a tinted background plus a 3px left border accent in the brand primary
 * color so the eye lands on the current page even when the rail is dense.
 */

import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import {
  Link,
} from '@tanstack/react-router';
import {
  getHomeHref,
  getNavigationGrouped,
  isDestinationActive,
  type PortalContext,
  type ShellUser,
} from '../../lib/navigation.js';
import { NavIcon } from './NavIcon.js';
import { SidebarProfile } from './SidebarProfile.js';

export interface SidebarProps {
  context: PortalContext;
  pathname: string;
  user: ShellUser | null;
  /** Tablet behavior: when true, the rail collapses to icon-only until the
   *  user toggles it open. Desktop ignores this and stays full-width. */
  defaultCollapsed?: boolean;
}

export function Sidebar({
  context,
  pathname,
  user,
  defaultCollapsed = false,
}: SidebarProps): JSX.Element | null {
  // Public visitors don't get a sidebar — top nav only.
  if (context === 'public') return null;

  // The collapsed state lives here (not in a parent) because the sidebar is
  // the only thing that needs it. Tablet starts collapsed; desktop ignores.
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);

  const sections = getNavigationGrouped(context);
  const homeHref = getHomeHref(context);

  return (
    <aside
      className={`sidebar ${collapsed ? 'sidebar--collapsed' : 'sidebar--expanded'}`}
      data-context={context}
      aria-label="Menu utama"
      data-testid="sidebar"
    >
      <header className="sidebar-header">
        <Link className="sidebar-brand" to={homeHref}>
          <img
            className="brand-logo"
            src="/images/brand/coding-class-logo-purple.png"
            alt="Coding Class TerusMengajar"
            width={36}
            height={20}
          />
          {!collapsed && (
            <>
              <span className="brand-divider" aria-hidden="true" />
              <span className="brand-product">Coding Class</span>
            </>
          )}
        </Link>
        <button
          type="button"
          className="sidebar-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Buka menu' : 'Tutup menu'}
          aria-expanded={!collapsed}
          data-testid="sidebar-collapse-toggle"
        >
          <NavIcon icon={collapsed ? 'menu' : 'menu'} size={20} />
        </button>
      </header>

      <nav className="sidebar-nav" role="navigation" aria-label="Menu utama">
        {sections.map((section) => (
          <div key={section.label} className="sidebar-section">
            {!collapsed && <h3 className="sidebar-section-label">{section.label}</h3>}
            <ul className="sidebar-section-items" role="list">
              {section.items.map((destination) => {
                const active = isDestinationActive(destination, pathname);
                if (destination.href) {
                  return (
                    <li key={destination.label}>
                      <Link
                        to={destination.href}
                        className={`sidebar-link ${active ? 'is-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        title={collapsed ? destination.label : undefined}
                        data-testid={`sidebar-link-${destination.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="sidebar-link-icon">
                          <NavIcon icon={destination.icon} size={20} />
                        </span>
                        {!collapsed && (
                          <span className="sidebar-link-label">{destination.label}</span>
                        )}
                        {!collapsed && destination.badge !== undefined && (
                          <span className="sidebar-link-badge">{destination.badge}</span>
                        )}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={destination.label}>
                    <span
                      className="sidebar-link is-disabled"
                      aria-disabled="true"
                      title={destination.disabledReason ?? 'Segera tersedia'}
                    >
                      <span className="sidebar-link-icon">
                        <NavIcon icon={destination.icon} size={20} />
                      </span>
                      {!collapsed && (
                        <span className="sidebar-link-label">{destination.label}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <footer className="sidebar-footer">
        {user && <SidebarProfile user={user} context={context} compact={collapsed} />}
      </footer>
    </aside>
  );
}
